import {
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  FileText,
  Search,
  MessageSquareText,
  ShieldAlert,
  UserCheck,
  Users,
  XCircle
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Sidebar } from '../components/Sidebar'
import { hasInternalApi } from '../lib/internalApi'
import {
  buildAliasCandidateRows,
  buildLineImportDetail,
  buildLineImportMessageView,
  type LineAliasCandidateRow,
  type LineImportDetailMessageGroup,
  type LineImportDetailMessageRow,
  type LineImportDetailRoleFilter
} from '../lib/lineImportDetail'
import { useManagers } from '../lib/useManagers'
import { useMyManager } from '../lib/useManagerProfile'
import {
  useCreateLineSenderAlias,
  useDecideLineSenderAliasCandidate,
  useLineImportDetail
} from '../lib/useLineImports'

export function LineImportDetailPage() {
  const { lineImportId } = useParams<{ lineImportId: string }>()
  const apiEnabled = hasInternalApi()
  const detail = useLineImportDetail(lineImportId)
  const myManager = useMyManager()
  const managers = useManagers()
  const createAlias = useCreateLineSenderAlias(lineImportId)
  const decideAliasCandidate = useDecideLineSenderAliasCandidate(lineImportId)
  const [searchText, setSearchText] = useState('')
  const [senderFilter, setSenderFilter] = useState('all')
  const [roleFilter, setRoleFilter] = useState<LineImportDetailRoleFilter>('all')
  const [hideEvents, setHideEvents] = useState(false)
  const [selectedManagerId, setSelectedManagerId] = useState('')
  const [aliasError, setAliasError] = useState<string | null>(null)
  const [aliasSuccess, setAliasSuccess] = useState<string | null>(null)
  const [candidateError, setCandidateError] = useState<string | null>(null)
  const [candidateSuccess, setCandidateSuccess] = useState<string | null>(null)
  const model = detail.data
    ? buildLineImportDetail({
      ...detail.data.line_import,
      sender_aliases: detail.data.sender_aliases
    }, detail.data.messages)
    : null
  const unresolvedSenderRows = model?.senderRows.filter((row) => row.speakerRole === 'other') ?? []
  const aliasCandidateRows = detail.data
    ? buildAliasCandidateRows(
      detail.data.alias_candidates,
      managers.data ?? [],
      detail.data.line_import.liver ?? null
    )
    : []
  const managerAliasTargetId = selectedManagerId || myManager.data?.id || managers.data?.[0]?.id || ''
  const messageView = useMemo(
    () => model
      ? buildLineImportMessageView(model, {
        query: searchText,
        sender: senderFilter === 'all' ? undefined : senderFilter,
        role: roleFilter,
        hideEvents
      })
      : null,
    [hideEvents, model, roleFilter, searchText, senderFilter]
  )

  return (
    <div className="flex min-h-full">
      <Sidebar />
      <main className="min-w-0 flex-1 bg-gray-50 px-4 py-6 pt-16 sm:px-6 md:pt-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <Link
                to="/line"
                className="mb-3 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4" />
                LINE一覧へ戻る
              </Link>
              <h1 className="truncate text-2xl font-semibold text-gray-900">
                {model?.liverName ?? 'LINE履歴'}
              </h1>
              <p className="mt-1 text-sm leading-6 text-gray-500">
                保存済みのLINE履歴を、担当候補・リスク・日別メッセージで確認します。
              </p>
            </div>
            {model && (
              <Link
                to={`/liver/${model.lineImport.liver_id}`}
                className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                ライバー詳細
              </Link>
            )}
          </header>

          {!apiEnabled && (
            <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              LINE履歴詳細には Internal API 設定が必要です。
            </div>
          )}

          {detail.isLoading && (
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-sm text-gray-400">
              LINE履歴を読み込み中...
            </div>
          )}

          {detail.error && (
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
              LINE履歴の取得に失敗: {detail.error instanceof Error ? detail.error.message : String(detail.error)}
            </div>
          )}

          {model && (
            <>
              <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <DetailMetric
                  icon={<MessageSquareText className="h-4 w-4" />}
                  label="メッセージ"
                  value={`${model.messages.length.toLocaleString()}件`}
                  note={`保存値 ${model.lineImport.message_count.toLocaleString()}件`}
                />
                <DetailMetric
                  icon={<CalendarDays className="h-4 w-4" />}
                  label="期間"
                  value={model.periodLabel}
                  note={`${model.lineImport.active_days} active days`}
                />
                <DetailMetric
                  icon={<Users className="h-4 w-4" />}
                  label="担当候補"
                  value={model.primaryManagerName ?? '-'}
                  note={model.senderRows.slice(0, 2).map((row) => `${row.sender} ${row.messageCount}`).join(' / ') || '-'}
                />
                <DetailMetric
                  icon={<ShieldAlert className="h-4 w-4" />}
                  label="注意"
                  value={`${model.riskCount}件`}
                  note={`引継ぎ ${model.handoffSignalCount}件`}
                />
              </section>

              <section className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                      <FileText className="h-4 w-4 text-gray-400" />
                      ファイル情報
                    </h2>
                    <dl className="mt-3 space-y-2 text-sm">
                      <InfoRow label="ファイル" value={model.lineImport.file_name} />
                      <InfoRow label="保存日時" value={formatDateTime(model.lineImport.created_at)} />
                      <InfoRow label="トピック" value={model.topicLabels.join(' / ') || '-'} />
                    </dl>
                    {model.lineImport.handoff_note && (
                      <div className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        {model.lineImport.handoff_note}
                      </div>
                    )}
                  </div>

                  {aliasCandidateRows.length > 0 && (
                    <div className="rounded-lg border border-sky-100 bg-white p-4">
                      <div className="flex items-center justify-between gap-3">
                        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                          <UserCheck className="h-4 w-4 text-sky-500" />
                          LINE名変更候補
                        </h2>
                        <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                          要確認 {aliasCandidateRows.length}
                        </span>
                      </div>
                      <div className="mt-3 space-y-2">
                        {aliasCandidateRows.map((row) => (
                          <AliasCandidateCard
                            key={row.id}
                            row={row}
                            disabled={decideAliasCandidate.isPending}
                            onAccept={() => handleDecideAliasCandidate(row, 'accept')}
                            onReject={() => handleDecideAliasCandidate(row, 'reject')}
                          />
                        ))}
                      </div>
                      {candidateSuccess && (
                        <p className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {candidateSuccess}
                        </p>
                      )}
                      {candidateError && (
                        <p className="mt-3 text-xs text-red-600">{candidateError}</p>
                      )}
                    </div>
                  )}

                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between gap-3">
                      <h2 className="text-sm font-semibold text-gray-900">発言者別</h2>
                      {unresolvedSenderRows.length > 0 && (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                          未紐付け {unresolvedSenderRows.length}
                        </span>
                      )}
                    </div>
                    <ul className="mt-3 space-y-1.5">
                      {model.senderRows.slice(0, 8).map((row) => (
                        <li key={row.sender}>
                          <button
                            type="button"
                            onClick={() => setSenderFilter((current) => current === row.sender ? 'all' : row.sender)}
                            className={`flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm ${
                              senderFilter === row.sender ? 'bg-sky-50 text-sky-800' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <span className="min-w-0 truncate">{row.sender}</span>
                            <span className="flex shrink-0 items-center gap-1.5">
                              <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${roleBadgeClass(row.speakerRole)}`}>
                                {roleLabel(row.speakerRole)}
                              </span>
                              <span className="tabular-nums text-gray-500">{row.messageCount}件</span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    {unresolvedSenderRows.length > 0 && (
                      <div className="mt-4 rounded-lg border border-amber-100 bg-amber-50 px-3 py-3">
                        <div className="flex items-start gap-2 text-xs text-amber-800">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <p>
                            LINE名を紐付けると、同じ名前の過去メッセージもマネージャー/ライバーとして表示されます。
                          </p>
                        </div>
                        <label className="mt-3 block text-xs font-medium text-amber-900">
                          マネージャーとして紐付ける先
                          <select
                            value={managerAliasTargetId}
                            onChange={(event) => setSelectedManagerId(event.target.value)}
                            className="mt-1 w-full rounded-md border border-amber-200 bg-white px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-amber-300 focus:ring-2 focus:ring-amber-100"
                          >
                            {managers.data?.map((manager) => (
                              <option key={manager.id} value={manager.id}>
                                {manager.name || manager.email}
                              </option>
                            ))}
                          </select>
                        </label>
                        <div className="mt-3 space-y-2">
                          {unresolvedSenderRows.slice(0, 5).map((row) => (
                            <div key={row.sender} className="rounded-md bg-white/80 px-2 py-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="min-w-0 truncate text-xs font-medium text-gray-800">{row.sender}</span>
                                <span className="shrink-0 text-[11px] text-gray-500">{row.messageCount}件</span>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                <button
                                  type="button"
                                  disabled={!managerAliasTargetId || createAlias.isPending}
                                  onClick={() => handleCreateAlias(row.sender, 'manager')}
                                  className="rounded-md bg-sky-600 px-2 py-1 text-[11px] font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                                >
                                  マネージャー
                                </button>
                                <button
                                  type="button"
                                  disabled={createAlias.isPending}
                                  onClick={() => handleCreateAlias(row.sender, 'liver')}
                                  className="rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                                >
                                  ライバー
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        {aliasSuccess && (
                          <p className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {aliasSuccess}
                          </p>
                        )}
                        {aliasError && (
                          <p className="mt-3 text-xs text-red-600">{aliasError}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white">
                  <div className="space-y-3 border-b border-gray-200 px-4 py-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h2 className="text-sm font-semibold text-gray-900">メッセージ</h2>
                        <p className="mt-1 text-xs text-gray-500">
                          {messageView?.messages.length.toLocaleString() ?? 0} / {model.messages.length.toLocaleString()}件
                        </p>
                      </div>
                      {(searchText || senderFilter !== 'all' || roleFilter !== 'all' || hideEvents) && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchText('')
                            setSenderFilter('all')
                            setRoleFilter('all')
                            setHideEvents(false)
                          }}
                          className="w-fit rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                        >
                          解除
                        </button>
                      )}
                    </div>
                    <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_auto]">
                      <label className="relative block">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                        <input
                          type="search"
                          value={searchText}
                          onChange={(event) => setSearchText(event.target.value)}
                          placeholder="本文 / 発言者 / 種別で検索"
                          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                        />
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {ROLE_FILTERS.map((filter) => (
                          <button
                            key={filter.value}
                            type="button"
                            onClick={() => setRoleFilter(filter.value)}
                            className={`rounded-lg px-2.5 py-2 text-xs ${
                              roleFilter === filter.value
                                ? 'bg-slate-900 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {filter.label}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setHideEvents((value) => !value)}
                          className={`rounded-lg px-2.5 py-2 text-xs ${
                            hideEvents ? 'bg-slate-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          イベント非表示
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="max-h-[70vh] overflow-y-auto px-4 py-4">
                    {messageView && messageView.dayGroups.length > 0 ? (
                      <div className="space-y-6">
                        {messageView.dayGroups.map((group) => (
                          <section key={group.date} className="space-y-2">
                            <div className="sticky top-0 z-10 bg-white/95 py-1 text-xs font-semibold text-gray-500">
                              {group.date}
                            </div>
                            {group.messageGroups.map((messageGroup) => (
                              <MessageGroup key={messageGroup.id} group={messageGroup} />
                            ))}
                          </section>
                        ))}
                      </div>
                    ) : (
                      <p className="py-8 text-sm text-gray-400">条件に合うメッセージがありません。</p>
                    )}
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  )

  async function handleCreateAlias(lineName: string, entityType: 'manager' | 'liver') {
    setAliasError(null)
    setAliasSuccess(null)
    try {
      const response = await createAlias.mutateAsync(entityType === 'manager'
        ? {
          line_name: lineName,
          entity_type: 'manager',
          manager_id: managerAliasTargetId
        }
        : {
          line_name: lineName,
          entity_type: 'liver',
          liver_id: model?.lineImport.liver_id
        })
      setAliasSuccess(`${lineName} を紐付けました (${response.updated_message_count}件更新)`)
      setSenderFilter('all')
      setRoleFilter('all')
    } catch (error) {
      setAliasError(error instanceof Error ? error.message : String(error))
    }
  }

  async function handleDecideAliasCandidate(
    row: LineAliasCandidateRow,
    action: 'accept' | 'reject'
  ) {
    setCandidateError(null)
    setCandidateSuccess(null)
    try {
      const response = await decideAliasCandidate.mutateAsync({
        candidateId: row.id,
        action
      })
      if (action === 'accept') {
        setCandidateSuccess(`${row.targetLabel} → ${row.observedName} に変更しました (${response.updated_message_count}件更新)`)
      } else {
        setCandidateSuccess(`${row.observedName} の候補を却下しました`)
      }
      setSenderFilter('all')
      setRoleFilter('all')
    } catch (error) {
      setCandidateError(error instanceof Error ? error.message : String(error))
    }
  }
}

const ROLE_FILTERS: Array<{ value: LineImportDetailRoleFilter; label: string }> = [
  { value: 'all', label: 'すべて' },
  { value: 'manager', label: 'マネージャー' },
  { value: 'liver', label: 'ライバー' },
  { value: 'other', label: '未紐付け' },
  { value: 'system', label: 'システム' }
]

function DetailMetric({
  icon,
  label,
  value,
  note
}: {
  icon: React.ReactNode
  label: string
  value: string
  note: string
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
          {icon}
        </span>
      </div>
      <p className="mt-4 truncate text-xl font-semibold text-gray-900">{value}</p>
      <p className="mt-1 truncate text-xs text-gray-500">{note}</p>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-3">
      <dt className="text-gray-400">{label}</dt>
      <dd className="min-w-0 truncate text-gray-700">{value}</dd>
    </div>
  )
}

function AliasCandidateCard({
  row,
  disabled,
  onAccept,
  onReject
}: {
  row: LineAliasCandidateRow
  disabled: boolean
  onAccept: () => void
  onReject: () => void
}) {
  const changeFromLabel = row.previousLineName ?? row.targetLabel
  return (
    <article className="rounded-lg border border-gray-100 bg-slate-50 px-3 py-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-gray-900">
            <span className="max-w-[140px] truncate">{changeFromLabel}</span>
            <span className="text-gray-400">→</span>
            <span className="max-w-[140px] truncate">{row.observedName}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-600 ring-1 ring-gray-200">
              {row.entityTypeLabel}
            </span>
            <span className="text-[11px] text-gray-500">信頼度 {row.confidenceLabel}</span>
          </div>
          <p className="mt-2 text-xs leading-5 text-gray-600">{row.reason}</p>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <button
            type="button"
            disabled={disabled}
            onClick={onAccept}
            className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-2 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            変更
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={onReject}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
          >
            <XCircle className="h-3.5 w-3.5" />
            却下
          </button>
        </div>
      </div>
    </article>
  )
}

function MessageGroup({ group }: { group: LineImportDetailMessageGroup }) {
  if (group.bubbleSide === 'center') {
    return (
      <div className="space-y-1.5">
        {group.messages.map((message) => (
          <MessageRow key={message.id} message={message} />
        ))}
      </div>
    )
  }

  const isManager = group.bubbleSide === 'right'
  return (
    <article className={`flex ${isManager ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[78%] sm:max-w-[68%] ${isManager ? 'items-end' : 'items-start'} flex flex-col`}>
        <div className={`mb-1 flex flex-wrap items-center gap-1.5 ${isManager ? 'justify-end' : 'justify-start'}`}>
          <span className="max-w-[180px] truncate text-xs font-semibold text-gray-700">
            {group.sender}
          </span>
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${roleBadgeClass(group.speakerRole)}`}>
            {roleLabel(group.speakerRole)}
          </span>
        </div>
        <div className="space-y-1">
          {group.messages.map((message, index) => (
            <MessageBubble
              key={message.id}
              message={message}
              first={index === 0}
              last={index === group.messages.length - 1}
            />
          ))}
        </div>
        <div className={`mt-1 text-[11px] text-gray-400 ${isManager ? 'text-right' : 'text-left'}`}>
          {formatGroupTime(group)}
        </div>
      </div>
    </article>
  )
}

function MessageRow({ message }: { message: LineImportDetailMessageRow }) {
  if (message.bubbleSide === 'center') {
    return (
      <article className="flex justify-center py-1">
        <div className="inline-flex max-w-[78%] items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500">
          <AlertTriangle className="h-3 w-3" />
          <span className="font-medium">{message.messageTypeLabel}</span>
          <span>{formatMessageTime(message.sent_time)}</span>
          <span className="truncate">{message.body}</span>
        </div>
      </article>
    )
  }

  return (
    <article className={`flex ${message.bubbleSide === 'right' ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[78%] sm:max-w-[68%] ${message.bubbleSide === 'right' ? 'items-end' : 'items-start'} flex flex-col`}>
        <MessageBubble message={message} first last />
        <div className={`mt-1 text-[11px] text-gray-400 ${message.bubbleSide === 'right' ? 'text-right' : 'text-left'}`}>
          {formatMessageTime(message.sent_time)}
        </div>
      </div>
    </article>
  )
}

function MessageBubble({
  message,
  first,
  last
}: {
  message: LineImportDetailMessageRow
  first: boolean
  last: boolean
}) {
  const isManager = message.bubbleSide === 'right'
  const roundedCorner = isManager
    ? first ? 'rounded-tr-sm' : last ? 'rounded-br-sm' : 'rounded-r-md'
    : first ? 'rounded-tl-sm' : last ? 'rounded-bl-sm' : 'rounded-l-md'
  return (
    <div className={`rounded-2xl px-3.5 py-2.5 shadow-sm ${roundedCorner} ${
      isManager
        ? 'bg-sky-600 text-white'
        : message.speakerRole === 'liver'
          ? 'bg-emerald-50 text-gray-800 ring-1 ring-emerald-100'
          : 'bg-white text-gray-800 ring-1 ring-gray-100'
    }`}
    >
      {message.message_type !== 'text' && (
        <div className={`mb-1 text-[11px] ${isManager ? 'text-sky-100' : 'text-gray-500'}`}>
          {message.messageTypeLabel}
        </div>
      )}
      <p className={`whitespace-pre-wrap break-words text-sm leading-6 ${
        isManager ? 'text-white' : 'text-gray-800'
      }`}
      >
        {message.body || message.messageTypeLabel}
      </p>
    </div>
  )
}

function roleLabel(role: LineImportDetailMessageRow['speakerRole']): string {
  if (role === 'manager') return 'マネージャー候補'
  if (role === 'liver') return 'ライバー候補'
  if (role === 'system') return 'システム'
  return '未紐付け'
}

function roleBadgeClass(role: LineImportDetailMessageRow['speakerRole']): string {
  if (role === 'manager') return 'bg-sky-50 text-sky-700'
  if (role === 'liver') return 'bg-emerald-50 text-emerald-700'
  if (role === 'system') return 'bg-gray-100 text-gray-500'
  return 'bg-amber-50 text-amber-700'
}

function formatGroupTime(group: LineImportDetailMessageGroup): string {
  const first = formatMessageTime(group.firstTime)
  const last = formatMessageTime(group.lastTime)
  return first === last ? first : `${first}〜${last}`
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatMessageTime(value: string | null): string {
  if (!value) return '--:--'
  return value.slice(0, 5)
}
